import React, { useEffect, useState, useCallback } from 'react';
import {
  Popover, Box, Typography, IconButton, Button, Divider, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FollowTheSignsIcon from '@mui/icons-material/FollowTheSigns';
import { useNavigate } from 'react-router-dom';

const TIPO_ICONO = {
  recordatorio_24h: <ScheduleIcon sx={{ fontSize: 18, color: '#f59e0b' }} />,
  recordatorio_2h: <ScheduleIcon sx={{ fontSize: 18, color: '#dc2626' }} />,
  cita_nueva: <EventIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />,
  cita_confirmada: <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />,
  cita_cancelada: <CancelIcon sx={{ fontSize: 18, color: '#ef4444' }} />,
  seguimiento: <FollowTheSignsIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />,
};

function tiempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `hace ${days}d`;
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
}

export default function NotificationsPopover({ anchorEl, open, onClose, onChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [marking, setMarking] = useState(false);

  const fetchNotifs = useCallback(async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    setLoading(true);
    try {
      const r = await fetch('/api/notificaciones/?limit=20', {
        headers: { 'X-User-Id': String(userId) },
      });
      if (!r.ok) {
        setItems([]);
        return;
      }
      const data = await r.json();
      setItems(data.notificaciones || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchNotifs();
  }, [open, fetchNotifs]);

  const marcarLeida = async (id) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    try {
      await fetch(`/api/notificaciones/${id}/leer`, {
        method: 'PUT',
        headers: { 'X-User-Id': String(userId) },
      });
    } catch {}
  };

  const onClickItem = async (n) => {
    if (!n.leida) {
      await marcarLeida(n.id);
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, leida: true } : it)));
      onChange && onChange();
    }
    onClose();
    if (n.cita_id) {
      navigate(`/agenda?cita=${n.cita_id}`);
    }
  };

  const onMarcarTodas = async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    setMarking(true);
    try {
      await fetch('/api/notificaciones/leer-todas', {
        method: 'PUT',
        headers: { 'X-User-Id': String(userId) },
      });
      setItems((prev) => prev.map((it) => ({ ...it, leida: true })));
      onChange && onChange();
    } catch {}
    setMarking(false);
  };

  const noLeidas = items.filter((it) => !it.leida).length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 360,
          maxHeight: 520,
          ml: 1,
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{
        px: 2, py: 1.2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        color: '#fff',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Notificaciones</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85 }}>
            {noLeidas > 0 ? `${noLeidas} sin leer` : 'Todo al día'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 80 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography sx={{ fontSize: 13 }}>Sin notificaciones</Typography>
          </Box>
        ) : (
          items.map((n, idx) => (
            <React.Fragment key={n.id}>
              {idx > 0 && <Divider sx={{ mx: 2 }} />}
              <Box
                onClick={() => onClickItem(n)}
                sx={{
                  px: 2, py: 1.2,
                  display: 'flex', gap: 1.2,
                  cursor: 'pointer',
                  bgcolor: n.leida ? 'transparent' : 'rgba(14, 165, 233, 0.05)',
                  borderLeft: n.leida ? '3px solid transparent' : '3px solid #0ea5e9',
                  '&:hover': { bgcolor: '#f1f5f9' },
                  transition: 'background 0.15s',
                }}
              >
                <Box sx={{ flexShrink: 0, mt: 0.4 }}>
                  {TIPO_ICONO[n.tipo] || <NotificationsNoneIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 12.5,
                    fontWeight: n.leida ? 500 : 700,
                    color: '#0f172a',
                    lineHeight: 1.3,
                    mb: 0.2,
                  }}>
                    {n.titulo}
                  </Typography>
                  <Typography sx={{
                    fontSize: 11.5,
                    color: '#64748b',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {n.mensaje}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.4 }}>
                    {tiempoRelativo(n.created_at)}
                    {n.paciente_nombre ? ` · ${n.paciente_nombre}` : ''}
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))
        )}
      </Box>

      {items.length > 0 && noLeidas > 0 && (
        <Box sx={{ borderTop: '1px solid #e2e8f0', p: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={onMarcarTodas}
            disabled={marking}
            sx={{
              fontSize: 12, textTransform: 'none', color: '#1e3a5f', fontWeight: 600,
            }}
          >
            Marcar todas como leídas
          </Button>
        </Box>
      )}
    </Popover>
  );
}
